/* Microsoft Azure AD sign-in interstitial.

   Shown before launching a sub-supplier PunchOut when the supplier has
   `requires_ad_login=true` (e.g. Grainger Advantage — they apply
   company-specific pricing based on the buyer's corporate identity).

   Two modes, toggled by the presence of MSAL env vars:

     1. Real MSAL OAuth (when REACT_APP_MSAL_TENANT_ID +
        REACT_APP_MSAL_CLIENT_ID are set):
        Uses `@azure/msal-browser` to popup the real Microsoft login
        flow. The returned ID/access token is forwarded to the backend
        audit endpoint.

     2. Demo interstitial (env vars NOT set):
        Renders a Microsoft-branded form that accepts the buyer's
        corporate email and password. The form is intentionally close
        enough to the real Azure AD UX to set buyer expectations, but
        every visit is clearly labelled "DEMO MODE" with an amber
        notice so nobody mistakes it for a live SSO bridge.

   In both modes the backend persists the captured UPN to
   `ad_signin_audit` and issues a one-shot token the launch endpoint
   validates. The UPN is then embedded in the outbound cXML as
   `<Extrinsic name="UserEmail">…</Extrinsic>` and
   `<Extrinsic name="UniqueName">…</Extrinsic>`.
*/
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MSAL_TENANT_ID = process.env.REACT_APP_MSAL_TENANT_ID;
const MSAL_CLIENT_ID = process.env.REACT_APP_MSAL_CLIENT_ID;
const MSAL_ENABLED = !!(MSAL_TENANT_ID && MSAL_CLIENT_ID);

let _msalInstance = null;
async function getMsal() {
  if (_msalInstance) return _msalInstance;
  const { PublicClientApplication } = await import("@azure/msal-browser");
  _msalInstance = new PublicClientApplication({
    auth: {
      clientId: MSAL_CLIENT_ID,
      authority: `https://login.microsoftonline.com/${MSAL_TENANT_ID}`,
      redirectUri: window.location.origin,
    },
    cache: { cacheLocation: "sessionStorage", storeAuthStateInCookie: false },
  });
  await _msalInstance.initialize();
  return _msalInstance;
}

export default function MicrosoftADSignInDialog({
  open,
  onOpenChange,
  supplier,
  session,
  onSignedIn,
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [step, setStep] = useState("email"); // email | password
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setPassword("");
      setName("");
      setStep("email");
    }
  }, [open]);

  const submitDemoCredentials = async (e) => {
    e?.preventDefault?.();
    if (step === "email") {
      if (!email || !email.includes("@")) {
        toast.error("Enter a valid corporate email");
        return;
      }
      setStep("password");
      return;
    }
    if (!password) {
      toast.error("Enter your corporate password");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/punchout/ad/sign-in`, {
        email,
        name: name || email.split("@")[0],
        supplier_id: supplier?.id,
        network_id: session?.network_id,
        company_name: session?.company_name,
      });
      onSignedIn?.({ ad_token: data.ad_token, email, name: data.name });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  const signInWithRealMsal = async () => {
    setBusy(true);
    try {
      const msal = await getMsal();
      const result = await msal.loginPopup({
        scopes: ["openid", "profile", "email", "User.Read"],
        prompt: "select_account",
      });
      const account = result.account;
      const upn = account?.username || account?.idTokenClaims?.preferred_username;
      const fullName = account?.name;
      const { data } = await axios.post(`${BACKEND_URL}/api/punchout/ad/sign-in`, {
        email: upn,
        name: fullName,
        supplier_id: supplier?.id,
        network_id: session?.network_id,
        company_name: session?.company_name,
        msal_access_token: result.accessToken,
        msal_id_token: result.idToken,
      });
      onSignedIn?.({ ad_token: data.ad_token, email: upn, name: fullName });
    } catch (err) {
      toast.error(err?.errorMessage || err?.message || "Microsoft sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  if (!supplier) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="ad-signin-dialog">
        <div className="bg-white">
          {/* Microsoft-style header strip */}
          <div className="px-6 pt-6 pb-3 flex items-center gap-2 border-b border-slate-100">
            <MicrosoftLogo />
            <span className="text-sm font-semibold text-slate-700">Microsoft</span>
            <button
              onClick={() => onOpenChange(false)}
              className="ml-auto text-slate-400 hover:text-slate-700 p-1"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div className="px-6 py-5">
            <DialogHeader className="space-y-1">
              <DialogTitle className="text-xl font-display font-semibold text-slate-900">
                {step === "email" ? "Sign in" : "Enter password"}
              </DialogTitle>
              <DialogDescription className="text-[12px] text-slate-500 leading-snug">
                Continue to <span className="font-semibold text-slate-800">{supplier.name}</span> with your
                corporate identity. Your sign-in is used to apply
                company-specific pricing.
              </DialogDescription>
            </DialogHeader>

            {!MSAL_ENABLED && (
              <div
                className="mt-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900 leading-snug"
                data-testid="ad-demo-mode-notice"
              >
                <span className="font-semibold">DEMO MODE.</span> Real Microsoft SSO activates as soon as
                <code className="mx-1 text-[10px] bg-amber-100 px-1 rounded">REACT_APP_MSAL_TENANT_ID</code>
                and
                <code className="mx-1 text-[10px] bg-amber-100 px-1 rounded">REACT_APP_MSAL_CLIENT_ID</code>
                are set in <code className="text-[10px] bg-amber-100 px-1 rounded">.env</code>.
              </div>
            )}

            <form onSubmit={submitDemoCredentials} className="mt-4 space-y-3">
              {step === "email" ? (
                <>
                  <label className="block">
                    <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                      Email, phone, or Skype
                    </div>
                    <Input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="someone@company.com"
                      data-testid="ad-signin-email"
                      autoFocus
                      type="email"
                      className="h-10"
                    />
                  </label>
                  <div className="text-[11px] text-slate-500">
                    Can&apos;t access your account? Use your buyer email.
                  </div>
                </>
              ) : (
                <>
                  <div className="text-[12px] text-slate-700 flex items-center gap-2 px-3 py-2 rounded-md bg-slate-50 border border-slate-200">
                    <span className="font-semibold">{email}</span>
                    <button
                      type="button"
                      onClick={() => setStep("email")}
                      className="ml-auto text-[#0067B8] hover:underline text-[11px]"
                    >
                      Change
                    </button>
                  </div>
                  <label className="block">
                    <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                      Password
                    </div>
                    <Input
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      data-testid="ad-signin-password"
                      autoFocus
                      type="password"
                      className="h-10"
                    />
                  </label>
                  <label className="block">
                    <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                      Display name <span className="text-slate-400 normal-case font-normal">(optional)</span>
                    </div>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Jane Smith"
                      data-testid="ad-signin-name"
                      className="h-10"
                    />
                  </label>
                </>
              )}

              <div className="pt-2 flex items-center justify-end">
                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="ad-signin-next"
                  className="bg-[#0067B8] hover:bg-[#005ba1] text-white font-semibold px-5 h-9"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : step === "email" ? "Next" : "Sign in"}
                </Button>
              </div>
            </form>

            {MSAL_ENABLED && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <Button
                  onClick={signInWithRealMsal}
                  disabled={busy}
                  data-testid="ad-signin-msal"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold h-9 text-sm"
                >
                  {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <ShieldCheck size={14} className="mr-1.5" />}
                  Sign in with real Microsoft 365
                </Button>
                <div className="text-[10px] text-slate-400 mt-1">
                  Uses tenant <code className="font-mono">{MSAL_TENANT_ID?.slice(0, 8)}…</code>
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 text-[10px] text-slate-500 leading-snug">
            Continue to {supplier.name} · cXML PunchOut · Identity {supplier.code}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}
